import React, { createContext, useContext, useState } from 'react';

export type SidebarPosition = 'left' | 'right';
export type SidebarStyle = 'default' | 'evo';
export type DisplayDensity = 'comfortable' | 'compact';
export type ThemePreference = 'light' | 'dark' | 'system';
export type ProjectCreationWorkflow = 'killer' | 'legacy';

interface UserPreferences {
  sidebarPosition: SidebarPosition;
  sidebarStyle: SidebarStyle;
  showWelcomeDialog: boolean;
  defaultLandingPage: string;
  rememberLastPage: boolean;
  lastVisitedPage?: string;
  displayDensity: DisplayDensity;
  keyboardShortcutsEnabled: boolean;
  theme: ThemePreference;
  projectCreationWorkflow: ProjectCreationWorkflow;
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (newPreferences: Partial<UserPreferences>) => void;
}

export const defaultPreferences: UserPreferences = {
  sidebarPosition: 'left',
  sidebarStyle: 'default',
  showWelcomeDialog: true,
  defaultLandingPage: 'dashboard',
  rememberLastPage: false,
  displayDensity: 'comfortable',
  keyboardShortcutsEnabled: true,
  theme: 'light', // Always default to light mode for Evo workspace embedding
  projectCreationWorkflow: 'killer', // New "Killer UX" workflow is the default
};

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const STORAGE_KEY = 'user-preferences';

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    // Load preferences from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaultPreferences, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Failed to load user preferences:', error);
    }
    return defaultPreferences;
  });

  const updatePreferences = (newPreferences: Partial<UserPreferences>) => {
    setPreferences((prev) => {
      const updated = { ...prev, ...newPreferences };
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save user preferences:', error);
      }
      return updated;
    });
  };

  return (
    <UserPreferencesContext.Provider value={{ preferences, updatePreferences }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
